import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  COMPARISON_OPTIONS, CreditsSurveyAnswersDTO, CURRENT_METHOD_OPTIONS, LESSON_PRICE, LESSONS_USED_OPTIONS,
  PRICE_MONTHLY_OPTIONS, SURVEY_BONUS_CREDITS, SURVEY_MISSING_MAX_LENGTH, TIME_SAVED_OPTIONS,
} from '@superlekcja/shared'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { rememberReturnPath, takeReturnPath } from '@/lib/return-after-login'
import { fetchCreditsSurveyStatus, submitCreditsSurvey } from './survey.service'

export const CREDITS_SURVEY_PATH = '/ankieta'

type ChoiceAnswers = Pick<CreditsSurveyAnswersDTO, 'lessonsUsed' | 'timeSaved' | 'currentMethod' | 'comparison' | 'priceMonthly'>

const QUESTIONS: { field: keyof ChoiceAnswers; label: string; options: readonly string[] }[] = [
  { field: 'lessonsUsed', label: 'q-lessons-used', options: LESSONS_USED_OPTIONS },
  { field: 'timeSaved', label: 'q-time-saved', options: TIME_SAVED_OPTIONS },
  { field: 'currentMethod', label: 'q-current-method', options: CURRENT_METHOD_OPTIONS },
  { field: 'comparison', label: 'q-comparison', options: COMPARISON_OPTIONS },
  { field: 'priceMonthly', label: 'q-price-monthly', options: PRICE_MONTHLY_OPTIONS },
]

const bonus = { bonus: SURVEY_BONUS_CREDITS, lessons: SURVEY_BONUS_CREDITS / LESSON_PRICE }

export function CreditsSurveyPage() {
  const { t } = useTranslation()
  const { isAuthenticated, withUser } = useAuth()

  // Zalogowal sie tutaj (okno logowania mailem nie przeladowuje strony) — sciezka powrotu
  // jest juz zbedna i nie moze pozniej przekierowac z glownej.
  useEffect(() => { if (isAuthenticated) takeReturnPath() }, [isAuthenticated])

  const login = () => {
    rememberReturnPath(CREDITS_SURVEY_PATH)
    withUser(() => {})
  }

  return (
    <div className="container mx-auto max-w-2xl px-4 py-10">
      <h1 className="font-display text-3xl font-bold">{t('credits-survey.title', bonus)}</h1>
      <p className="mt-3 text-sl-ink-2">{t('credits-survey.intro', bonus)}</p>
      <div className="mt-8">
        {isAuthenticated ? <SurveyForUser /> : (
          <div className="flex flex-col items-start gap-4 rounded-sl-card bg-sl-surface p-6 shadow-sl-card">
            <p>{t('credits-survey.login-required')}</p>
            <Button onClick={login} data-testid="survey-login">{t('credits-survey.login')}</Button>
          </div>
        )}
      </div>
    </div>
  )
}

function SurveyForUser() {
  const { t } = useTranslation()
  const status = useQuery({ queryKey: ['credits-survey-status'], queryFn: fetchCreditsSurveyStatus })

  if (status.isPending) return null
  // Glosno: bez statusu nie wiemy, czy wolno przyznac kredyty, wiec formularza nie pokazujemy.
  if (status.isError) return <Notice>{t('credits-survey.submit-failed')}</Notice>
  if (status.data !== 'eligible') return <Notice testId="survey-status">{t(`credits-survey.status-${status.data}`, bonus)}</Notice>
  return <SurveyForm />
}

function SurveyForm() {
  const { t } = useTranslation()
  const { refreshProfile } = useAuth()
  const [choices, setChoices] = useState<Partial<ChoiceAnswers>>({})
  const [missing, setMissing] = useState('')
  const [interviewOk, setInterviewOk] = useState(false)
  const [showIncomplete, setShowIncomplete] = useState(false)

  const submit = useMutation({
    mutationFn: submitCreditsSurvey,
    onSuccess: result => { if (result === 'granted') refreshProfile() },
  })

  if (submit.data === 'granted') {
    return (
      <div className="flex flex-col items-start gap-4 rounded-sl-card bg-sl-surface p-6 shadow-sl-card" data-testid="survey-thanks">
        <h2 className="font-display text-2xl font-bold">{t('credits-survey.thanks-title')}</h2>
        <p>{t('credits-survey.thanks', bonus)}</p>
        <Button asChild><Link to="/">{t('credits-survey.back-home')}</Link></Button>
      </div>
    )
  }
  // Stan zmienil sie miedzy otwarciem strony a wyslaniem (np. druga karta) — mowimy prawde.
  if (submit.data) return <Notice testId="survey-status">{t(`credits-survey.status-${submit.data}`, bonus)}</Notice>

  const complete = QUESTIONS.every(q => choices[q.field])
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!complete) { setShowIncomplete(true); return }
    submit.mutate({
      ...(choices as ChoiceAnswers),
      ...(missing.trim() ? { missing: missing.trim() } : {}),
      interviewOk,
    })
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6" data-testid="credits-survey-form">
      {QUESTIONS.map(q => (
        <fieldset key={q.field} className="rounded-sl-card bg-sl-surface p-5 shadow-sl-card">
          <legend className="float-left mb-3 w-full font-semibold">{t(`credits-survey.${q.label}`)}</legend>
          <div className="clear-both flex flex-col gap-2">
            {q.options.map(option => (
              <label key={option} className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name={q.field}
                  value={option}
                  checked={choices[q.field] === option}
                  onChange={() => setChoices(prev => ({ ...prev, [q.field]: option }))}
                  className="h-4 w-4 accent-primary"
                />
                {t(`credits-survey.${q.field}.${option}`)}
              </label>
            ))}
          </div>
        </fieldset>
      ))}

      <div className="flex flex-col gap-3 rounded-sl-card bg-sl-surface p-5 shadow-sl-card">
        <label htmlFor="survey-missing" className="font-semibold">{t('credits-survey.q-missing')}</label>
        <Textarea id="survey-missing" value={missing} maxLength={SURVEY_MISSING_MAX_LENGTH} onChange={e => setMissing(e.target.value)} />
        <label className="flex cursor-pointer items-center gap-3">
          <Checkbox checked={interviewOk} onCheckedChange={value => setInterviewOk(value === true)} />
          {t('credits-survey.q-interview')}
        </label>
      </div>

      {showIncomplete && !complete && <p className="text-destructive" role="alert">{t('credits-survey.answer-all')}</p>}
      {submit.isError && <p className="text-destructive" role="alert">{t('credits-survey.submit-failed')}</p>}
      <Button type="submit" disabled={submit.isPending} className="self-start" data-testid="survey-submit">
        {t('credits-survey.submit', bonus)}
      </Button>
    </form>
  )
}

function Notice({ children, testId }: { children: React.ReactNode; testId?: string }) {
  return <p className="rounded-sl-card bg-sl-surface p-6 shadow-sl-card" data-testid={testId}>{children}</p>
}
